import { useEffect, useMemo, useState } from "react";
import { SearchableSelect } from "@/components/shared/SearchableSelect";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import client, { getErrorMessage } from "@/api/client";
import { toast } from "sonner";
import { Upload, FileText } from "lucide-react";
import { AttributionField } from "./shared/AttributionField";
import VendorFormDialog from "../vendorMaster/VendorFormDialog";

const pad = (n) => String(n).padStart(2, "0");
const iso = (y, m, d) => `${y}-${pad(m + 1)}-${pad(d)}`;

// Rent cycle starting this month: day D -> D of this month to D-1 of next month.
function cycleDates(day) {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();
  const startDay = Math.min(day || 1, new Date(y, m + 1, 0).getDate());
  const nextStart = new Date(y, m + 1, Math.min(day || 1, new Date(y, m + 2, 0).getDate()));
  nextStart.setDate(nextStart.getDate() - 1);
  return { from: iso(y, m, startDay), to: iso(nextStart.getFullYear(), nextStart.getMonth(), nextStart.getDate()) };
}

export default function BillFormDialog({ bill, refData, readOnly, onClose, onSaved }) {
  useEffect(() => { refData.refresh(["vendors", "sites", "equipment"]); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const isEdit = !!bill;
  // Once any payment is linked, amount/vendor/attribution become locked —
  // "cancel and re-enter instead" per spec's assumptions.
  const locked = readOnly || (isEdit && bill.has_linked_payments);
  const [saving, setSaving] = useState(false);
  const [invoiceFile, setInvoiceFile] = useState(null);
  const [showAddVendor, setShowAddVendor] = useState(false);
  const [form, setForm] = useState({
    vendor_code: bill?.vendor_code || "",
    entity_code: bill?.entity_code || "",
    site_id: bill?.site_id || "",
    equipment_id: bill?.equipment_id || "",
    amount: bill?.amount ?? "",
    bill_date: bill?.bill_date ? bill.bill_date.slice(0, 10) : new Date().toISOString().slice(0, 10),
    category_id: bill?.category_id || "",
    remarks: bill?.remarks || "",
    accommodation_id: bill?.accommodation_id || "",
    bill_from: bill?.bill_from || "",
    bill_to: bill?.bill_to || "",
  });
  const [rooms, setRooms] = useState([]);
  const vendor = refData.vendors.find((v) => v.vendor_code === form.vendor_code);
  const isAcc = vendor?.category_code === "ACC";
  const room = rooms.find((r) => r.room_id === form.accommodation_id);
  const vendorOptions = useMemo(() => [
    ...refData.vendors.map((v) => ({ value: v.vendor_code, label: v.vendor_name, searchText: v.vendor_code, testId: `bill-form-vendor-option-${v.vendor_code}` })),
    ...(!locked ? [{ value: "__add_vendor__", label: "+ Add New Vendor", pinned: true, className: "text-amber-600 font-medium", testId: "bill-form-add-new-vendor" }] : []),
  ], [refData.vendors, locked]);

  useEffect(() => {
    if (!isAcc) { setRooms([]); return; }
    client.get("/accommodation/rooms/for-vendor", { params: { vendor_code: form.vendor_code } })
      .then((res) => setRooms(res.data))
      .catch(() => setRooms([]));
  }, [form.vendor_code, isAcc]);

  const pickRoom = (roomId) => {
    const r = rooms.find((x) => x.room_id === roomId);
    if (!r) { setForm((f) => ({ ...f, accommodation_id: "", bill_from: "", bill_to: "" })); return; }
    const { from, to } = cycleDates(r.cycle_start_day);
    setForm((f) => ({ ...f, accommodation_id: roomId, bill_from: from, bill_to: to, amount: r.monthly_rent, site_id: r.site_id, equipment_id: "" }));
  };

  const set = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  const viewInvoice = async () => {
    try {
      const res = await client.get(`/payment-desk/bills/${bill.bill_id}/invoice`, { responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank");
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.vendor_code) return toast.error("Vendor is required");
    if (!form.entity_code) return toast.error("Entity is required");
    if (!form.site_id && !form.equipment_id) return toast.error("Select a Site or Equipment");
    if (!form.category_id) return toast.error("Category is required");
    if (form.amount === "" || Number(form.amount) <= 0) return toast.error("Bill Amount must be greater than 0");
    setSaving(true);
    const fd = new FormData();
    fd.append("vendor_code", form.vendor_code);
    fd.append("entity_code", form.entity_code);
    if (form.site_id) fd.append("site_id", form.site_id);
    if (form.equipment_id) fd.append("equipment_id", form.equipment_id);
    fd.append("amount", form.amount);
    fd.append("bill_date", form.bill_date);
    fd.append("category_id", form.category_id);
    if (form.remarks.trim()) fd.append("remarks", form.remarks.trim());
    if (isAcc && form.accommodation_id) {
      fd.append("accommodation_id", form.accommodation_id);
      if (form.bill_from) fd.append("bill_from", form.bill_from);
      if (form.bill_to) fd.append("bill_to", form.bill_to);
    }
    if (invoiceFile) fd.append("invoice", invoiceFile);
    try {
      const res = isEdit
        ? await client.put(`/payment-desk/bills/${bill.bill_id}`, fd)
        : await client.post("/payment-desk/bills", fd);
      toast.success(isEdit ? "Vendor Bill updated" : `Vendor Bill created — ${res.data.bill_id}`);
      onSaved(res.data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="bill-form-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isEdit ? `Vendor Bill — ${bill.bill_id}` : "Add Vendor Bill"}
            {isEdit && <Badge className="bg-slate-500">{bill.status}</Badge>}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          {isEdit && locked && (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2" data-testid="bill-form-locked-note">
              {readOnly
                ? "View only — this bill has linked payments and can no longer be edited."
                : "This bill has linked payments and can no longer be edited — cancel and re-enter instead."}
            </p>
          )}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">Vendor *</label>
              <SearchableSelect testId="bill-form-vendor-select" loading={refData.loadingLists.vendors} value={form.vendor_code} onValueChange={(v) => (v === "__add_vendor__" ? setShowAddVendor(true) : setForm((f) => ({ ...f, vendor_code: v, accommodation_id: "", bill_from: "", bill_to: "" })))} disabled={locked} placeholder="Select vendor"
  options={vendorOptions} />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">Entity *</label>
              <SearchableSelect testId="bill-form-entity-select" loading={refData.loadingLists.entities} value={form.entity_code} onValueChange={(v) => set("entity_code", v)} disabled={locked} placeholder="Select entity"
  options={refData.entities.map((en) => ({ value: en.code, label: en.name, testId: `bill-form-entity-option-${en.code}` }))} />
            </div>
          </div>

          {isAcc && (
            <div className="rounded-md border border-amber-200 bg-amber-50/50 p-3 space-y-3" data-testid="bill-form-accommodation-section">
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">Accommodation (Room)</label>
                <SearchableSelect testId="bill-form-accommodation-select" value={form.accommodation_id || "__none__"} onValueChange={(v) => pickRoom(v === "__none__" ? "" : v)} disabled={locked} placeholder="Select room (optional)"
                  options={[
                    { value: "__none__", label: "None (not a room rent bill)", pinned: true },
                    ...(form.accommodation_id && !room ? [{ value: form.accommodation_id, label: form.accommodation_id }] : []),
                    ...rooms.map((r) => ({ value: r.room_id, label: `${r.room_id} — ${r.site_label} (₹${Number(r.monthly_rent).toLocaleString("en-IN")})`, testId: `bill-form-accommodation-option-${r.room_id}` })),
                  ]} />
              </div>
              {form.accommodation_id && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1.5 block">Bill From</label>
                    <Input type="date" data-testid="bill-form-from-input" value={form.bill_from} onChange={(e) => set("bill_from", e.target.value)} disabled={locked} />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1.5 block">Bill To</label>
                    <Input type="date" data-testid="bill-form-to-input" value={form.bill_to} onChange={(e) => set("bill_to", e.target.value)} disabled={locked} />
                  </div>
                </div>
              )}
            </div>
          )}

          <fieldset disabled={locked} className="contents">
            <AttributionField
              siteId={form.site_id}
              equipmentId={form.equipment_id}
              sites={refData.sites}
              equipment={refData.equipment} sitesLoading={refData.loadingLists.sites} equipmentLoading={refData.loadingLists.equipment}
              onChange={({ siteId, equipmentId }) => setForm((f) => ({ ...f, site_id: siteId, equipment_id: equipmentId }))}
              testId="bill-form-attribution"
            />
          </fieldset>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">Bill Amount (₹) *</label>
              <Input type="number" step="any" data-testid="bill-form-amount-input" value={form.amount} onChange={(e) => set("amount", e.target.value)} disabled={locked} />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">Bill Date *</label>
              <Input type="date" data-testid="bill-form-date-input" value={form.bill_date} onChange={(e) => set("bill_date", e.target.value)} disabled={locked} />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">Category *</label>
              <SearchableSelect testId="bill-form-category-select" loading={refData.loadingLists.categories} value={form.category_id} onValueChange={(v) => set("category_id", v)} disabled={locked} placeholder="Select category"
  options={refData.categories.map((c) => ({ value: c.id, label: c.name, testId: `bill-form-category-option-${c.id}` }))} />
            </div>
          </div>

          <div>
            <label className="text-sm font-medium text-slate-700 mb-1.5 block">Remarks</label>
            <Textarea data-testid="bill-form-remarks-input" value={form.remarks} onChange={(e) => set("remarks", e.target.value)} disabled={readOnly} />
          </div>

          <div>
            <label className="text-sm font-medium text-slate-700 mb-1.5 block">Invoice</label>
            {isEdit && bill.invoice_file && (
              <button type="button" onClick={viewInvoice} data-testid="bill-form-view-invoice" className="flex items-center gap-1.5 text-xs text-amber-600 hover:underline mb-2">
                <FileText className="h-3.5 w-3.5" /> {bill.invoice_file.filename}
              </button>
            )}
            {!readOnly && (
              <label className="inline-flex items-center gap-1.5 text-xs cursor-pointer text-amber-600 hover:text-amber-700" data-testid="bill-form-invoice-upload">
                <Upload className="h-3.5 w-3.5" /> {invoiceFile ? invoiceFile.name : isEdit ? "Replace Invoice" : "Upload Invoice (optional)"}
                <input type="file" hidden accept=".pdf,.jpg,.jpeg,.png,.webp,.heic" onChange={(e) => e.target.files[0] && setInvoiceFile(e.target.files[0])} />
              </label>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} data-testid="bill-form-cancel-button">
              {readOnly ? "Close" : "Cancel"}
            </Button>
            {!readOnly && (
              <Button type="submit" disabled={saving || locked} data-testid="bill-form-submit-button" className="bg-amber-600 hover:bg-amber-700">
                {saving ? "Saving..." : isEdit ? "Save Changes" : "Create Vendor Bill"}
              </Button>
            )}
          </DialogFooter>
        </form>
      </DialogContent>

      {showAddVendor && (
        <VendorFormDialog
          vendor={null}
          onClose={() => setShowAddVendor(false)}
          onSaved={(newVendor) => {
            setShowAddVendor(false);
            set("vendor_code", newVendor.vendor_code);
          }}
        />
      )}
    </Dialog>
  );
}
